// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Pure layout maths for the hand-rolled SVG charts. No DOM here — every
// function takes numbers and returns positions, so the tests can assert
// in-bounds, no-overlap, flush and no-NaN properties directly.
//
// Area-only tests pass on visually broken layouts, so the tests that ship with
// this file assert POSITIONS.

export interface Rect { x: number; y: number; w: number; h: number }
export interface Pt { x: number; y: number }

export interface Scale { (v: number): number; domain: [number, number]; range: [number, number] }

export function linear(domain: [number, number], range: [number, number]): Scale {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  const span = d1 - d0;
  const f = ((v: number) => (span === 0 ? r0 : r0 + ((v - d0) / span) * (r1 - r0))) as Scale;
  f.domain = domain;
  f.range = range;
  return f;
}

/** log10 scale, guarding non-positive input by clamping to the domain floor. */
export function log(domain: [number, number], range: [number, number]): Scale {
  const d0 = Math.max(domain[0], 1e-9);
  const d1 = Math.max(domain[1], d0 * 10);
  const l0 = Math.log10(d0);
  const l1 = Math.log10(d1);
  const [r0, r1] = range;
  const f = ((v: number) => {
    const x = Math.log10(Math.max(v, d0));
    return r0 + ((x - l0) / (l1 - l0)) * (r1 - r0);
  }) as Scale;
  f.domain = [d0, d1];
  f.range = range;
  return f;
}

/** "Nice" tick values covering a domain, at most `count` of them. */
export function ticks(min: number, max: number, count = 5): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return [min];
  const raw = (max - min) / Math.max(1, count);
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  const step = (norm >= 5 ? 10 : norm >= 2 ? 5 : norm >= 1 ? 2 : 1) * mag;
  const out: number[] = [];
  for (let t = Math.ceil(min / step) * step; t <= max + step * 1e-9; t += step) out.push(Number(t.toFixed(10)));
  return out;
}

/**
 * Vertical bars across a band scale. Segments are FLUSH: the gap is taken out
 * of the bar, never out of the band, so the bands tile the axis exactly.
 */
export function barBands(n: number, x0: number, width: number, gap = 0.25): { x: number; w: number; cx: number }[] {
  if (n <= 0) return [];
  const band = width / n;
  const w = Math.max(0, band * (1 - gap));
  const pad = (band - w) / 2;
  return Array.from({ length: n }, (_, i) => {
    const x = x0 + band * i + pad;
    return { x, w, cx: x + w / 2 };
  });
}

/** Bar rectangles anchored to a baseline, handling negative values. */
export function bars(values: (number | null)[], box: Rect, domain: [number, number], gap = 0.25): (Rect | null)[] {
  const y = linear(domain, [box.y + box.h, box.y]);
  const base = y(Math.max(domain[0], Math.min(domain[1], 0)));
  const bands = barBands(values.length, box.x, box.w, gap);
  return values.map((v, i) => {
    if (v === null || !Number.isFinite(v)) return null;
    const top = y(v);
    const b = bands[i];
    return { x: b.x, y: Math.min(top, base), w: b.w, h: Math.abs(base - top) };
  });
}

/** Points on a line/scatter, with nulls preserved as gaps. */
export function linePoints(values: (number | null)[], box: Rect, domain: [number, number]): (Pt | null)[] {
  const y = linear(domain, [box.y + box.h, box.y]);
  const n = values.length;
  const step = n > 1 ? box.w / (n - 1) : 0;
  return values.map((v, i) => (v === null || !Number.isFinite(v) ? null : { x: box.x + step * i, y: y(v) }));
}

/**
 * Split a point series into unbroken runs. A null is a GAP, never a zero and
 * never a straight line drawn across missing years — that is the defect that
 * makes a suppressed cost series look like a crash to the floor.
 */
export function runs(points: (Pt | null)[]): Pt[][] {
  const out: Pt[][] = [];
  let cur: Pt[] = [];
  for (const p of points) {
    if (p) cur.push(p);
    else if (cur.length) { out.push(cur); cur = []; }
  }
  if (cur.length) out.push(cur);
  return out;
}

export function path(points: Pt[]): string {
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ');
}

/** Stacked bands for an area chart: each series stacked on the one before. */
export function stack(series: number[][]): number[][] {
  const n = series[0]?.length ?? 0;
  const acc = new Array(n).fill(0);
  return series.map((row) => row.map((v, i) => (acc[i] += v || 0)));
}

/** Grid cells for a matrix heatmap. Flush: cells tile the box with no gaps. */
export function gridCells(rows: number, cols: number, box: Rect): Rect[] {
  if (rows <= 0 || cols <= 0) return [];
  const cw = box.w / cols;
  const ch = box.h / rows;
  const out: Rect[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) out.push({ x: box.x + c * cw, y: box.y + r * ch, w: cw, h: ch });
  }
  return out;
}

/**
 * Bin values into a histogram. Bins are contiguous and flush across
 * [min, max]; the top edge is inclusive on the last bin only, so no value is
 * counted twice and none is dropped.
 */
export function histogram(values: number[], binCount: number): { lo: number; hi: number; count: number; items: number[] }[] {
  const finite = values.filter((v) => Number.isFinite(v));
  if (!finite.length || binCount <= 0) return [];
  const min = Math.min(...finite);
  const max = Math.max(...finite);
  const span = max - min || 1;
  const step = span / binCount;
  const bins = Array.from({ length: binCount }, (_, i) => ({
    lo: min + step * i,
    hi: i === binCount - 1 ? max : min + step * (i + 1),
    count: 0,
    items: [] as number[],
  }));
  for (const v of finite) {
    let idx = Math.floor((v - min) / step);
    if (idx >= binCount) idx = binCount - 1;
    if (idx < 0) idx = 0;
    bins[idx].count++;
    bins[idx].items.push(v);
  }
  return bins;
}

/**
 * Nudge overlapping labels apart along one axis while keeping their order.
 * Used for the scatter's division labels and the dumbbell rows.
 */
export function declutter(positions: number[], minGap: number, lo: number, hi: number): number[] {
  const order = positions.map((p, i) => ({ p, i })).sort((a, b) => a.p - b.p);
  let prev = -Infinity;
  for (const item of order) {
    const want = Math.max(item.p, prev + minGap);
    item.p = Math.min(Math.max(want, lo), hi);
    prev = item.p;
  }
  // A second pass from the top keeps everything inside the box when the
  // bottom-up pass has pushed the tail past `hi`.
  let next = Infinity;
  for (let k = order.length - 1; k >= 0; k--) {
    order[k].p = Math.min(order[k].p, next - minGap);
    next = order[k].p;
  }
  const out = new Array(positions.length).fill(0);
  for (const item of order) out[item.i] = item.p;
  return out;
}
